import { useEffect, useMemo, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { Formik } from "formik";
import PostForm from "../components/PostForm";
import { initialValues, schema } from "../../../../constants/forms/post/edit";
import Submit from "../../../../components/FormikForm/Submit";
import Link from "../../../../components/Link";
import Grid from "../../../../components/Grid";
import Snackbar from "../../../../components/Snackbar";
import Loader from "../../../../components/LogoLoader";
import {
  editPost,
  resetEditPost,
  getPost,
} from "../../../../store/actions/post";
import { Text, Container, BottomActions, FormContainer, ContentWrapper } from "./styles"
import { CategoryDto } from "../../../../types/category";
import { Post } from "../../../../types/post";
import { Group } from "../../../../types";
import { useLayout } from "../../../../providers/LayoutProvider";
import { useWindowSize } from "../../../../hooks/useWindowSize";
import { getGroupById } from "../../../../store/actions/group";
import { hideModal, showModal } from "../../../../store/actions/modal";
import { setToZeroTimeZone } from "../../../../helpers/dates-hanlder";
import { ConnectedFocusError } from "../../../../components/ConnectedFocusError";
import { useFileUploadProgress } from "../../../../providers/FileUploader";
import ModalWaitAMinuteBis from "../New/components/ModalWaitAMinute";
import { extractMediaFileIdsFromContentObject } from "../../../../helpers/richtexteditor"
import PostFormV2 from "../components/PostFormV2";
import ConfirmPostEdition from "../components/ConfirmPostEdition";


interface ComponentProps {
  match;
  userInformation;
  allCategoriesInformation;
  allCategoriesInformationStates: {
    success: boolean;
    loading: boolean;
    error: boolean | string;
  };
  groupInformation: Group;
  groupInformationStates: {
    success: boolean;
    loading: boolean;
    error: boolean | string;
  };
  postInformation: Post;
  postInformationStates: {
    success: boolean;
    loading: boolean;
    error: boolean | string;
  };
  editPostStates: {
    success: boolean;
    loading: boolean;
    error: boolean | string;
  };
}

const Component = ({
  match,
  userInformation,
  allCategoriesInformation,
  allCategoriesInformationStates,
  groupInformation,
  groupInformationStates,
  postInformation,
  postInformationStates,
  editPostStates,
}: ComponentProps) => {
  const dispatch = useDispatch();
  const { isMobile } = useWindowSize();
  const { layout, setLayout } = useLayout();
  const [uploadingCount, setUploadingCount] = useState(0);
  const { uploadFiles, resetFileUploadProgress } = useFileUploadProgress();
  const [formSubmmited, setFormSubmmited] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  const timestamp = new Date().getTime();

  // Create mediaFilesMap from postInformation.body_attached_files
  const mediaFilesMap = useMemo(() => {
    const map: Record<number, any> = {};
    for (const file of postInformation?.body_attached_files || []) {
      map[file.id] = file;
    }
    return map;
  }, [postInformation]);

  const onSubmit = async ({ values, actions }) => {
    //Cuando migremos cover_pic podremos descomentar la linea de abajo
    //Debemos sumar la siguiente validacion "values.cover_pic?.some(file => file instanceof File"
    const thereAreFilesToUpload = values && values.attached_files?.some(file => file instanceof File);

    if (thereAreFilesToUpload) {
      dispatch(showModal("modal-wait-a-minute-bis"));
    }

    try {
      if (thereAreFilesToUpload) {
        const uploadedFiles = await uploadFiles(
          {
            //Cuando migremos cover_pic podremos descomentar la linea de abajo
            //...(values?.cover_pic && values?.cover_pic.length > 0 && { cover_pic: values?.cover_pic }),
            ...(values?.attached_files && values?.attached_files.length > 0 && { attached_files: values?.attached_files }),
          },
          userInformation.id,
          "post"
        );

        const updatedValues = {
          ...values,
          schedule_publish_at: values.schedule_publish_at ? setToZeroTimeZone(values.schedule_publish_at) : null,
          notificate_by_email: values.notificate_by_email.includes("true"),
          notificate_by_telegram: values.notificate_by_telegram.includes("true"),
          show_scheduled_post: values.show_scheduled_post.includes("true"),
          block_files_download: values.block_files_download.includes("true"),
          pinned: values.pinned.includes("true"),
          is_free: values.is_free.includes("true"),
          attached_files: uploadedFiles?.attached_files ? uploadedFiles?.attached_files : [],
          body_media_files_ids: extractMediaFileIdsFromContentObject(values.body)
        };

        dispatch(editPost(updatedValues));
      } else {
        // If there are no files to upload, dispatch newWelcomeContentPost directly
        const updatedValues = {
          ...values,
          schedule_publish_at: values.schedule_publish_at ? setToZeroTimeZone(values.schedule_publish_at) : null,
          notificate_by_email: values.notificate_by_email.includes("true"),
          notificate_by_telegram: values.notificate_by_telegram.includes("true"),
          show_scheduled_post: values.show_scheduled_post.includes("true"),
          block_files_download: values.block_files_download.includes("true"),
          pinned: values.pinned.includes("true"),
          is_free: values.is_free.includes("true"),
          attached_files: values?.attached_files,
          body_media_files_ids: extractMediaFileIdsFromContentObject(values.body)
        };

        dispatch(editPost(updatedValues));
      }
    } catch (error) {
      console.error('Error during file uploads:', error);
    } finally {
      dispatch(hideModal());
    }
  };

  useEffect(() => {
    if (!!postInformation && editPostStates.success) {
      resetFileUploadProgress();
      dispatch(hideModal());
      dispatch(showModal("modal-confirm-post-edition"));
      dispatch(resetEditPost());
    }
  }, [postInformation, editPostStates.success]);

  useEffect(() => {
    if (
      !!match.params &&
      (!groupInformation ||
        (!!groupInformation &&
          groupInformation.id !== Number(match.params.idGroup)))
    ) {
      dispatch(getGroupById({ group_id: match.params.idGroup }));
    }

    if (
      !!match.params &&
      (!postInformation ||
        (!!postInformation &&
          postInformation.id !== Number(match.params.idPost)))
    ) {
      dispatch(getPost({ idPost: match.params.idPost }));
    }
  }, [match]);

  useEffect(() => {
    setLayout({
      ...layout,
      page: {
        title: "Editar artículo",
        description: "Editar artículo",
      },
      dashboard: {
        tabs: {
          visible: false,
        },
      },
      header: {
        visible: true,
        actions: [
          <Link
            options={{
              type: "outline",
              size: "lg",
              skin: "purple",
              native: true,
            }}
            disabled={!groupInformation}
            href={
              groupInformation?.group_type === "free_experience"
                ? `/dashboard/community/events/${groupInformation?.id}/posts`
                : `/dashboard/${groupInformation?.group_type}/${groupInformation?.id}/posts`
            }
          >
            Cancelar
          </Link>,
          <Submit
            isSubmmiting={editPostStates.loading}
            options={{
              type: "filled",
              size: "lg",
              skin: "purple",
            }}
            disabled={!groupInformation}
            form="new-post-form"
          >
            Guardar
          </Submit>,
        ],
      },
    });
  }, [groupInformation, postInformation, editPostStates]);

  if (!postInformation || postInformationStates.loading) {
    return <Loader />;
  }

  const getBackUrl = () => {
    if (groupInformation?.group_type === "free_experience") {
      return `/dashboard/community/events/${groupInformation?.id}/posts`
    }
    return `/dashboard/${groupInformation?.group_type}/${groupInformation?.id}/posts`
  }

  return (
    <>
      <Container>
        <ContentWrapper>
          <FormContainer>
            <Formik
              initialValues={{
                ...initialValues,
                ...postInformation,
                notificate_by_email: postInformation.notificate_by_email ? ["true"] : [],
                notificate_by_telegram: postInformation.notificate_by_telegram ? ["true"] : [],
                block_files_download: postInformation.block_files_download ? ["true"] : [],
                pinned: postInformation.pinned ? ["true"] : [],
                show_scheduled_post: postInformation.show_scheduled_post ? ["true"] : [],
                is_free: postInformation.is_free ? ["true"] : [],
                content_categories: postInformation.content_categories?.map(
                  (item: CategoryDto) => item.id
                ),
                group: postInformation?.group?.id,
                cover_pic: postInformation?.cover_pic ? [postInformation?.cover_pic] : [],
              }}
              onSubmit={(values, actions) => {
                if (onSubmit) onSubmit({ values, actions });
              }}
              validateOnChange={false}
              validateOnBlur={formSubmmited}
              validationSchema={schema}
              enableReinitialize
            >
              {({ handleSubmit, ...rest }) => (
                <form
                  className="theme-form"
                  onSubmit={(event) => {
                    setFormSubmmited(true)
                    handleSubmit(event)
                  }}
                  id="new-post-form"
                >
                  <ConnectedFocusError />
                  <PostFormV2 {...rest} setUploadingCount={setUploadingCount} mediaFilesMap={mediaFilesMap} />
                  {!isMobile && (
                    <BottomActions>
                      <Submit
                        isSubmmiting={editPostStates.loading}
                        options={{
                          type: "filled",
                          size: "lg",
                          skin: "purple",
                        }}
                        form="new-post-form"
                      >
                        Guardar artículo
                      </Submit>
                      <Link
                        options={{
                          type: "outline",
                          size: "lg",
                          skin: "purple",
                          native: true,
                        }}
                        href={getBackUrl()}
                      >
                        Cancelar
                      </Link>
                    </BottomActions>
                  )}

                </form>
              )}

            </Formik>
          </FormContainer>
        </ContentWrapper>
      </Container>

      <ModalWaitAMinuteBis />
      <ConfirmPostEdition />

      <Snackbar
        visible={!!editPostStates.error}
        options={{
          time: 2000,
          type: "error",
        }}
      >
        Error al editar el artículo.
      </Snackbar>
    </>
  );
};

const state = ({ postStore, groupStore, categoryStore, userStore }) => {
  const { data: userInformation } = userStore.information;
  const { allCategories } = categoryStore;
  const {
    data: allCategoriesInformation,
    states: allCategoriesInformationStates,
  } = allCategories;
  const { data: groupInformation, states: groupInformationStates } =
    groupStore.group;
  const { data: postInformation, states: postInformationStates } =
    postStore.post;
  const { states: editPostStates } = postStore.editPost;

  return {
    userInformation,
    allCategoriesInformation,
    allCategoriesInformationStates,
    groupInformation,
    groupInformationStates,
    postInformation,
    postInformationStates,
    editPostStates,
  };
};

export default connect(state)(Component);
